import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { client } from '../../utils/contentfulClient';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { INLINES } from '@contentful/rich-text-types';
import { Helmet } from 'react-helmet-async';
import Carousel from 'react-bootstrap/Carousel';
import proj from './Project/Project.module.css';


const BRAND_COLOR = '#2c3e50';

function SingleProject() {
  const { slug } = useParams();  
  const [project, setProject] = useState(null);
  const [newerProj, setNewerProj] = useState(null);
  const [olderProj, setOlderProj] = useState(null);
  const [loading, setLoading] = useState(true);

  // Lightbox State (Simplified!)
  const [lightboxData, setLightboxData] = useState({ isOpen: false, images: [], currentIndex: 0 });

  const closeLightbox = () => setLightboxData({ ...lightboxData, isOpen: false });
  
  // This single function tells the Carousel what image is currently active
  const handleSelect = (selectedIndex) => {
    setLightboxData(prev => ({ ...prev, currentIndex: selectedIndex }));
  };

  useEffect(() => {
    setLoading(true);
    // Fetch the project and include depth: 2 to ensure we get the nested Image Group data!
    client.getEntries({ content_type: 'portfolioProject', 'fields.slug': slug, include: 2 })
    .then((response) => {
      const current = response.items[0];
      setProject(current);

      if (current) {
        const createdAt = current.sys.createdAt;
        const fetchNewer = client.getEntries({ content_type: 'portfolioProject', 'sys.createdAt[gt]': createdAt, order: 'sys.createdAt', limit: 1, 'fields.targetSite': process.env.REACT_APP_SITENAME });
        const fetchOlder = client.getEntries({ content_type: 'portfolioProject', 'sys.createdAt[lt]': createdAt, order: '-sys.createdAt', limit: 1, 'fields.targetSite': process.env.REACT_APP_SITENAME });

        Promise.all([fetchNewer, fetchOlder]).then(([newerRes, olderRes]) => {
          setNewerProj(newerRes.items[0] || null);
          setOlderProj(olderRes.items[0] || null);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    }).catch(console.error);
  }, [slug]);
  


  if (loading) return <p style={{ textAlign: 'center', marginTop: '50px' }}>Loading project...</p>;
  if (!project) return <p>Project not found!</p>;

  // NOTE: If app crashes here, check if fields in Contentful are "Text" vs "Rich Text" as discussed
  const { title, projectType, objectDimensions, coverImage, introduction, mainDescription, videoUrl, imageSet } = project.fields;

  // Intercept Asset Hyperlinks to open the Lightbox
  const richTextOptions = {
    renderNode: {
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        return (
          <span 
            onClick={(e) => {
              e.preventDefault();
              // The linked asset data is stored in node.data.target
              // We wrap it in an array so it perfectly matches the format your Lightbox expects!
              setLightboxData({
                isOpen: true,
                images: [node.data.target],
                currentIndex: 0
              });
            }}
            style={{ 
              color: BRAND_COLOR, 
              cursor: 'pointer', 
              textDecoration: 'underline',
              fontWeight: '500'
            }}
          >
            {children}
          </span>
        );
      }
    }
  };



  return (
    <article>
      <Helmet><title>{title} | Portfolio</title></Helmet>

      <div>                   
          <div className="container">

            <div style={{ marginTop: '30px' }}>              
              <Link to="/portfolio" style={{ textDecoration: 'none', color: BRAND_COLOR, textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.9rem' }}>
                &larr; Back to Portfolio
              </Link>
            </div>

              <div className="row">
                  <div className="col">
                      <div className={proj.name}>{title}</div>
                  </div>
              </div>
              <div className="row align-items-center row-cols-1 row-cols-md-2 ">
                  <div className="col">
                    {projectType && <span><div className={proj.subtitle}>Project Type</div><div className={proj.subtext}>{projectType}</div></span>}                    
                    {objectDimensions && <span><div className={proj.subtitle}>Dimensions</div><div className={proj.subtext}>{objectDimensions}</div></span>}
                  </div>
                  {introduction && (
                    <div className="col">                      
                      <div className={proj.subtext}>
                          {documentToReactComponents(introduction, richTextOptions)}
                      </div>
                  </div>
                  )}                 
              </div>
              <p></p>
              <div className="row">
                  <div className="col-md-12">
                      <div>
                        {coverImage && (
                          <figure>
                              <img className={proj.image} alt={title} src={`https:${coverImage.fields.file.url}`}></img>
                          </figure>                          
                        )}
                      </div>
                  </div>
                  <div className={proj.subtitle}>Conservation</div>
                  {mainDescription && (                    
                    <div className="elegant" style={{ width: '100%' }}>
                      {documentToReactComponents(mainDescription, richTextOptions)}
                    </div>
                  )}
              </div>                   

          {/* Image Set Rendering */}
          {imageSet && imageSet.length > 0 && (
            <div>
              <div className={proj.subtitle}>Project Gallery</div>                  
              <div className="row">
                {imageSet.map((group, groupIndex) => {
                  
                  // 1. Check the orientation field (defaulting to landscape if left blank)                  
                  const isPortrait = String(group.fields.orientation || '').toLowerCase().includes('portrait');
                  
                  // 2. Set the container size (Portraits share a row, Landscapes get a full row)
                  const colClass = isPortrait ? 'col-md-6' : 'col-md-12';
                  
                  // 3. Set the Contentful crop parameters so thumbnails are the correct shape
                  // Portrait = 3:4 ratio, Landscape = 3:2 ratio
                  const imgCropParams = isPortrait ? '?w=500&h=666&fit=fill' : '?w=800&h=533&fit=fill';
                  
                  // 4. Calculate width so 1, 2, or 3 images perfectly fill the flex container
                  const numImages = group.fields.images?.length || 1;
                  const imgWidth = numImages === 1 ? '100%' : numImages === 2 ? '48%' : '31%';

                 return (
                    <div key={groupIndex} className={colClass} style={{ marginBottom: '40px' }}>
                      
                      {/* 1. Changed flexWrap to 'nowrap' */}
                      <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                        
                        {group.fields.images?.map((img, imgIndex) => (
                          <img 
                            key={imgIndex} 
                            src={`https:${img.fields.file.url}${imgCropParams}`} 
                            alt="Gallery thumbnail"
                            style={{ 
                              cursor: 'pointer', 
                              flex: 1,           // 2. Tells Flexbox to divide the width equally
                              minWidth: 0,       // 3. Crucial Flexbox trick: allows images to shrink below their intrinsic size on tiny screens
                              width: '100%',     
                              objectFit: 'cover', 
                              borderRadius: '4px', 
                              transition: 'transform 0.2s' 
                            }}
                            onClick={() => setLightboxData({ isOpen: true, images: group.fields.images, currentIndex: imgIndex })}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          />
                        ))}

                      </div>
                      
                      {group.fields.description && (
                        <div style={{ textAlign: 'center', marginTop: '15px', color: '#666', fontStyle: 'italic', fontSize: '0.95rem' }}>
                          {group.fields.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}


          {/* Video Section (REVERTED TO IFRAME, HEIGHT 500) */}
          {videoUrl && (
            <div>
              <div className={proj.subtitle}>Project Video</div>          
              <iframe 
                width="100%" 
                height="500" 
                src={videoUrl.replace("watch?v=", "embed/")} 
                frameBorder="0" 
                allowFullScreen 
                title={`${title} Video`} 
                style={{ borderRadius: '8px' }}>
              </iframe>
            </div>
          )}
        

        {/* Lightbox Overlay using React Bootstrap Carousel */}
          {lightboxData.isOpen && (
            <div 
              onClick={closeLightbox} 
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
            >
              
              {/* NEW: Force Bootstrap's invisible control wrappers to drop the underline */}
              <style>
                {`
                  .carousel-control-prev,
                  .carousel-control-next {
                    text-decoration: none !important;
                  }
                  .carousel-control-prev:hover,
                  .carousel-control-next:hover {
                    text-decoration: none !important;
                  }
                `}
              </style>

              {/* Close Button */}
              <button 
                onClick={closeLightbox} 
                onMouseEnter={(e) => e.currentTarget.style.color = '#ccc'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'white'}
                style={{ position: 'absolute', top: '20px', right: '30px', background: 'none', border: 'none', color: 'white', fontSize: '2.5rem', cursor: 'pointer', transition: 'color 0.2s', zIndex: 1050 }}
              >
                &times;
              </button>

            {/* Carousel Container */}
              <div 
                style={{ width: '100vw', maxWidth: '1200px' }} 
                onClick={(e) => e.stopPropagation()} 
              >
                <Carousel 
                  activeIndex={lightboxData.currentIndex} 
                  onSelect={handleSelect}
                  interval={null} 
                  wrap={false}    
                  indicators={false}
                  
              // NEW: Custom High-Visibility Left Arrow (No Underline)
                  prevIcon={
                    <span aria-hidden="true" style={{
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.7)', 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%',
                      color: 'white', 
                      fontSize: '2rem',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                      textDecoration: 'none' // <-- Fixes the underline
                    }}>
                      &#10094;
                    </span>
                  }
                  
                  // NEW: Custom High-Visibility Right Arrow (No Underline)
                  nextIcon={
                    <span aria-hidden="true" style={{
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.7)', 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%',
                      color: 'white', 
                      fontSize: '2rem',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                      textDecoration: 'none' // <-- Fixes the underline
                    }}>
                      &#10095;
                    </span>
                  }
                >
                  {lightboxData.images.map((img, idx) => (
                    <Carousel.Item key={idx}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '85vh' }}>
                        
                        <img
                          className="d-block"
                          style={{ maxHeight: '70vh', maxWidth: '85vw', objectFit: 'contain', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}
                          src={`https:${img.fields.file.url}`}
                          alt={img.fields.title || "Gallery image"}
                        />
                        
                        {/* Image Caption */}
                        <div style={{ marginTop: '20px', textAlign: 'center', padding: '0 40px', color: '#fff', maxWidth: '700px' }}>
                          {img.fields.title && (
                            <div style={{ fontSize: '1.2rem', fontWeight: '400', letterSpacing: '1px' }}>
                              {img.fields.title}
                            </div>
                          )}
                          
                          {img.fields.description && (
                            <div style={{ color: '#ccc', marginTop: '8px', fontSize: '0.95rem', fontWeight: '300', fontStyle: 'italic', lineHeight: '1.4' }}>
                              {img.fields.description}
                            </div>
                          )}
                        </div>

                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
            </div>
          )}


          {/* Next/Prev Project Links */}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '30px', paddingBottom: '50px' }}>
            <div style={{ flex: 1, textAlign: 'left' }}>
              {newerProj && (
                <Link 
                  to={`/portfolio/${newerProj.fields.slug}`} 
                  style={{ textDecoration: 'none', color: BRAND_COLOR }}
                >
                  <span style={{ fontSize: '0.8rem', color: '#777', textTransform: 'uppercase' }}>&larr; Previous Project</span>
                  <div style={{ fontWeight: '400', marginTop: '8px', fontSize: '1.1rem' }}>{newerProj.fields.title}</div>
                </Link>
              )}
            </div>
            <div style={{ flex: 1, textAlign: 'right' }}>
              {olderProj && (
                <Link 
                  to={`/portfolio/${olderProj.fields.slug}`} 
                  style={{ textDecoration: 'none', color: BRAND_COLOR }}
                >
                  <span style={{ fontSize: '0.8rem', color: '#777', textTransform: 'uppercase' }}>Next Project &rarr;</span>
                  <div style={{ fontWeight: '400', marginTop: '8px', fontSize: '1.1rem' }}>{olderProj.fields.title}</div>
                </Link>
              )}
            </div>
          </div>

        </div>
      </div>
    </article>
  );
}

export default SingleProject;